import { ThemeOptions } from '@mui/material';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/lab/themeAugmentation';
const defaultBackgroundColor = '#EDEFF1';
const borderColor = '#9A9EA4';

type CustomTypographyKey = 'h7' | 'body4' | 'warning' | 'main' | 'body3U';

type CustomTypographyVariants = Record<
  CustomTypographyKey,
  React.CSSProperties
>;

declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface TypographyVariants extends CustomTypographyVariants {}

  // allow configuration using `createTheme`
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface TypographyVariantsOptions extends Partial<TypographyVariants> {}
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body4: true;
    warning: true;
    h7: true;
    main: true;
    body3U: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    outlinedSecondary: true;
    key: true;
    link: true;
  }
}
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    invisible: true;
    grey: true;
    yellow: true;
    green: true;
    blue: true;
    red: true;
    purple: true;
  }
}

export const root: ThemeOptions = {
  palette: {
    background: {
      default: defaultBackgroundColor,
    },
    text: {
      primary: '#5E6065',
      secondary: '#9A9EA4',
    },
    success: {
      main: '#6DBC44',
      light: '#f0fcf6',
      dark: '#05834a',
    },
    error: {
      dark: '#BD3030',
      main: '#D96666',
      light: '#FFE9E9',
    },

    grey: {
      100: '#F9F9F9',
      200: '#ECEEEF',
      400: '#9A9EA4',
      600: '#65686D',
    },
    warning: {
      light: '#FCEEE3',
      dark: '#A44A0A',
      main: '#f59d5f',
    },
    divider: borderColor,
  },
  typography: {
    main: {
      fontFamily: 'Montserrat',
      fontWeight: 800,
      fontSize: '6.5vw',
      textAlign: 'center',
      textTransform: 'capitalize',
      lineHeight: '6vw',
      zIndex: 111,
    },
    h1: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 500,
      fontSize: 64,
      lineHeight: '80px',
      letterSpacing: '-.01em',
    },
    h2: {
      fontWeight: 700,
      fontSize: 24,
      lineHeight: '32px',
      letterSpacing: '-.01em',
    },
    h3: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 700,
      fontSize: 40,
      lineHeight: '60px',
      textTransform: 'uppercase',
      letterSpacing: '-.01em',
    },
    h4: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 600,
      fontSize: 32,
      lineHeight: '39px',
    },
    h5: {
      fontWeight: 700,
      fontSize: 20,
      lineHeight: '30px',
    },
    h6: {
      fontWeight: 700,
      fontSize: 18,
      lineHeight: '24px',
    },
    h7: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 500,
      lineHeight: '80px',
      fontSize: 40,
      color: '#2E2E2E',
    },
    body1: {
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '22px',
    },
    body2: {
      fontWeight: 400,
      fontSize: 18,
      lineHeight: '24px',
    },
    body4: {
      display: 'inline-block',
      fontSize: 14,
      fontWeight: 500,
      textTransform: 'uppercase',
      color: '#9A9EA4',
      padding: '10px 0',
    },
    body3U: {
      fontFamily: 'Montserrat',
      textTransform: 'uppercase',
      fontWeight: 700,
    },
    warning: {
      marginLeft: 10,
      fontWeight: 500,
      fontSize: 17,
    },
  },
  components: {
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          '&$expanded': {
            margin: 0,
          },
          '&$expanded:before': {
            opacity: '1 !important',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          top: 2,
        },
      },
      defaultProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        BackdropProps: {
          sx: {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 16,
          backgroundColor: defaultBackgroundColor,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderWidth: 4,
          borderColor: '#ECEEEF',
        },
        rounded: {
          borderRadius: 5,
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: {
            variant: 'key',
          },
          style: {
            backgroundColor: 'white',
            textAlign: 'center',
            height: '100%',
            width: '100%',
            fontWeight: 500,
            fontFamily: 'Roboto',
            fontSize: '1.5rem !important',
            border: '1px solid #9A9EA4',
            boxShadow:
              '0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)',
            '& svg': {
              fontSize: 28,
            },
          },
        },
        {
          props: {
            variant: 'link',
          },
          style: {
            background: 'none',
            textDecoration: 'underline',
            fontSize: 'small',
            '&:active, &:focus': {
              textDecoration: 'underline',
            },
          },
        },
      ],
      defaultProps: {
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          transition: 'color .01s',
          padding: 8,
          borderRadius: 8,
          textTransform: 'capitalize',
          fontWeight: 700,
          fontSize: 16,
          '&:disabled': {
            backgroundColor: borderColor,
            color: 'white',
          },
        },
        sizeLarge: {
          fontWeight: 700,
          fontSize: 18,
          lineHeight: '30px',
        },
        contained: {
          padding: '8px 24px',
          borderRadius: '0',
          color: 'white',
          height: 56,
        },
        outlinedSecondary: {
          backgroundColor: 'white',
          '&:disabled': {
            backgroundColor: 'white',
            color: borderColor,
            border: `1px solid  ${borderColor}`,
          },
        },
        containedSizeLarge: {
          padding: '8px 24px',
          color: 'white',
        },
        outlined: {
          borderRadius: '0',
          borderWidth: '2px !important',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          borderRadius: 5,
          padding: '7.5px 8px',
        },
        inputRoot: {
          color: '#9A9EA4',
          fontSize: 20,
          fontWeight: 700,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: '#00000038',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        vertical: {
          marginLeft: 8,
          marginRight: 8,
          height: 'auto',
          backgroundColor: '#9A9EA4',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        scroller: {
          overflow: 'auto !important',
        },
        root: {
          backgroundColor: 'transparent',
          borderTopRightRadius: 5,
          borderTopLeftRadius: 5,
          width: 'calc(100% - 310px)',
        },
        indicator: {
          display: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          height: 65,
          minWidth: '205px !important',
          textTransform: 'uppercase',
          fontSize: 18,
          maxWidth: '90%',
          fontWeight: 700,
          display: 'flex',
          justifyContent: 'space-around',
          fontFamily: 'Montserrat',

          '&.Mui-selected': {
            border: `1px solid ${borderColor}`,
            borderBottom: 'white',
            backgroundColor: 'white',
            borderRadius: '8px 8px 0 0',
          },
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          padding: '0 !important',
          marginTop: '-1px',
          height: 'calc(100vh - 20px - 64px - 48px)',
          backgroundColor: 'white',
          border: `1px solid ${borderColor}`,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableFocusRipple: true,
      },
    },
    MuiStepButton: {
      defaultProps: {
        focusRipple: false,
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        lineHorizontal: {
          borderTopWidth: '3px !important',
        },
        alternativeLabel: {
          top: '125px !important',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontFamily: 'Montserrat',
          textTransform: 'uppercase',
          fontSize: '12pt',
        },
        iconContainer: {
          marginTop: '-30px',
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          borderRadius: '50%',
          padding: '1px',
          width: '25px',
          height: '25px',
          boxSizing: 'border-box',
        },
        text: {
          fontSize: '14px',
          fontWeight: 700,
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: {
            variant: 'grey',
          },
          style: {
            backgroundColor: '#ECEEEF',
          },
        },
        {
          props: {
            variant: 'yellow',
          },
          style: {
            backgroundColor: '#FFF6E3',
            color: '#C18800',
          },
        },
        {
          props: {
            variant: 'green',
          },
          style: {
            backgroundColor: '#F0FCF6',
            color: '#05834A',
          },
        },
        {
          props: {
            variant: 'blue',
          },
          style: {
            backgroundColor: '#F4F7FF',
            color: '#1842C8',
          },
        },
        {
          props: {
            variant: 'red',
          },
          style: {
            backgroundColor: '#FFE9E9',
            color: '#BD3030',
          },
        },
        {
          props: {
            variant: 'purple',
          },
          style: {
            backgroundColor: '#F8F4FF',
            color: '#4D13B9',
          },
        },
      ],
      styleOverrides: {
        root: {
          fontSize: 12,
          width: 'fit-content',
          borderRadius: 4,
          fontWeight: 600,
          height: 20,
          background: 'none',
        },
        avatar: {
          width: '40px !important',
          height: '40px !important',
          border: '1px solid #9e9e9e',
          marginRight: '-19px !important',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          maxWidth: '100vw',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontFamily: 'Montserrat, sans-serif',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeaders: {
          backgroundColor: defaultBackgroundColor,
        },
        columnHeader: {
          fontSize: 14,
        },
        pinnedColumnHeaders: {
          backgroundColor: defaultBackgroundColor,
          boxShadow: 'none',
        },
        pinnedColumns: {
          backgroundColor: '#fff',
          boxShadow: 'none',
        },
        columnSeparator: {
          display: 'none',
        },
        cell: {
          ':focus, :hover': {
            outline: 'none',
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
          fontSize: 8,

          '& .Mui-checked + .MuiSwitch-track': {
            '&:before': {
              content: '"ON"',
              left: 3,
              top: -5,
              fontWeight: 700,
            },
            '&:after': {
              content: '""',
              right: 4,
              fontWeight: 700,
              top: -5,
            },
          },

          '& .MuiSwitch-thumb': {
            boxShadow: 'none',
            backgroundColor: '#9A9EA4',
            boxSizing: 'border-box',
            border: `2px solid #fff`,
            width: '18px',
            height: '18px',
          },
        },
        track: {
          color: '#65686D',
          width: '32px',
          padding: 0,
          backgroundColor: '#ECEEEF',
          borderRadius: 22 / 2,
          height: '12px',
          opacity: 1,
          transform: 'translate(6px, 4px)',

          '&:before, &:after': {
            opacity: 1,
            position: 'absolute',
            height: 12,
          },
          '&:after': {
            content: '"OFF"',
            right: 4,
            fontWeight: 700,
            top: -5,
          },
        },
      },
    },
  },
};
